import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { AxiosError } from 'axios';
import * as _ from 'lodash';
import Cookies from 'js-cookie';
interface ApiError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
}

export interface Member {
  id: number;
  name: string | null;
  email: string;
  role: 'member' | 'admin';
  status: 'active' | 'pending';
  created_at: string;
  type: 'member' | 'invitation';
  expires_at: string | null;
  folder_ids?: number[]; // Add this line
  owner?: boolean;
}

export interface InvitationData {
  organization_id: number | null | undefined;
  email: string;
  role: 'member' | 'admin';
  folder_ids: number[];
}

interface MemberPermissions {
  user_id: number;
  email: string;
  role: 'member' | 'admin';
  folder_ids: number[];
}

interface Organization {
  id: number;
  name: string;
}

interface UserInvitation {
  id: number;
  organization: Organization;
  role: 'member' | 'admin';
  folder_count: number;
  sent_at: string;
  expires_at?: string;
  accepted_at?: string;
  status: 'pending' | 'accepted';
}

interface InvitationsState {
  pending: UserInvitation[];
  accepted: UserInvitation[];
}

interface MemberState {
  members: Member[];
  isLoading: boolean;
  isRemoving: boolean;
  error: string | null;
  selectedMemberPermissions: MemberPermissions | null;
  userInvitations: InvitationsState;
}

const initialState: MemberState = {
  members: [],
  isLoading: false,
  isRemoving: false,
  error: null,
  selectedMemberPermissions: null,
  userInvitations: {
    pending: [],
    accepted: [],
  },
};

export const fetchOrganizationMembers = createAsyncThunk(
  'members/fetchOrganizationMembers',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/members`
      );
      return response.data.members;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const sendInvitation = createAsyncThunk<
  Member,
  InvitationData,
  { rejectValue: ApiError }
>('members/sendInvitation', async (invitationData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/invitations', invitationData);
    return response.data.invitation;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue({
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        errors: error.response.data.errors,
      });
    }
    return rejectWithValue({
      status: 500,
      message: 'An unexpected error occurred',
    });
  }
});

export const fetchInvitationDetails = createAsyncThunk(
  'members/fetchInvitationDetails',
  async (invitationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/invitations/${invitationId}`);
      return response.data.invitation;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const updateInvitation = createAsyncThunk<
  Member,
  InvitationData & { invitation_id: number },
  { rejectValue: ApiError }
>('members/updateInvitation', async (invitationData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(
      `/invitations/${invitationData.invitation_id}`,
      invitationData
    );
    return response.data.invitation;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue({
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        errors: error.response.data.errors,
      });
    }
    return rejectWithValue({
      status: 500,
      message: 'An unexpected error occurred',
    });
  }
});

export const destroyInvitation = createAsyncThunk(
  'members/destroyInvitation',
  async (invitationId: number, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/invitations/${invitationId}`);
      return invitationId;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

// Add these new thunks
export const updateMember = createAsyncThunk<
  Member,
  {
    organizationId: number;
    userId: number;
    role: 'member' | 'admin';
    folder_ids: number[];
  },
  { rejectValue: ApiError }
>('members/updateMember', async (memberData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put(
      `/organizations/${memberData.organizationId}/members/${memberData.userId}`,
      {
        role: memberData.role,
        folder_ids: memberData.folder_ids,
      }
    );
    return response.data.member;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue({
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        errors: error.response.data.errors,
      });
    }
    return rejectWithValue({
      status: 500,
      message: 'An unexpected error occurred',
    });
  }
});

export const removeMember = createAsyncThunk<
  number,
  { organizationId: number; userId: number },
  { rejectValue: ApiError }
>('members/removeMember', async (memberData, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(
      `/organizations/${memberData.organizationId}/members/${memberData.userId}`
    );
    return memberData.userId;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue({
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        errors: error.response.data.errors,
      });
    }
    return rejectWithValue({
      status: 500,
      message: 'An unexpected error occurred',
    });
  }
});

export const fetchMemberDetails = createAsyncThunk<
  Member,
  { organizationId: number; userId: number },
  { rejectValue: string }
>(
  'members/fetchMemberDetails',
  async ({ organizationId, userId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/members/${userId}`
      );
      return response.data.member;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(
          error.response.data.message || 'Failed to fetch member details'
        );
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const fetchMemberPermissions = createAsyncThunk<
  MemberPermissions,
  { organizationId: string; memberId: string },
  { rejectValue: string }
>(
  'members/fetchMemberPermissions',
  async ({ organizationId, memberId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/organizations/${organizationId}/members/${memberId}/permissions`
      );
      return response.data.permissions;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(
          error.response.data.message || 'Failed to fetch member permissions'
        );
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const resendInvitation = createAsyncThunk<
  void,
  number,
  { rejectValue: ApiError }
>('members/resendInvitation', async (invitationId, { rejectWithValue }) => {
  try {
    await axiosInstance.post(`/invitations/${invitationId}/resend`);
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return rejectWithValue({
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        errors: error.response.data.errors,
      });
    }
    return rejectWithValue({
      status: 500,
      message: 'An unexpected error occurred',
    });
  }
});

export const fetchUserInvitations = createAsyncThunk<
  InvitationsState,
  void,
  { rejectValue: string }
>('members/fetchUserInvitations', async (_, { rejectWithValue }) => {
  try {
    const token = Cookies.get('accessToken');
    if (!token) {
      return rejectWithValue('Authentication required');
    }

    const response = await axiosInstance.get('/my-invitations', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.invitations;
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status === 401) {
        return rejectWithValue('Please login to view invitations');
      }
      return rejectWithValue(
        error.response.data.message || 'Failed to fetch invitations'
      );
    }
    return rejectWithValue('An unexpected error occurred');
  }
});

// Update the acceptInvitation thunk to include authorization
export const acceptInvitation = createAsyncThunk<
  { success: boolean },
  string,
  { rejectValue: ApiError }
>(
  'members/acceptInvitation',
  async (invitationToken, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.post('/user/invitations/accept', {
        token: invitationToken,
      });

      // If invitation accepted successfully, refresh the invitations list
      dispatch(fetchUserInvitations());
      return { success: true };
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue({
          status: error.response.status,
          message: error.response.data.message || 'An error occurred',
          errors: error.response.data.errors,
        });
      }
      return rejectWithValue({
        status: 500,
        message: 'An unexpected error occurred',
      });
    }
  }
);

const memberSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchOrganizationMembers.fulfilled,
        (state, action: PayloadAction<Member[]>) => {
          state.isLoading = false;
          state.members = action.payload;
        }
      )
      .addCase(fetchOrganizationMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(sendInvitation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        sendInvitation.fulfilled,
        (state, action: PayloadAction<Member>) => {
          state.isLoading = false;
          action.payload.status = 'pending';
          const newMember = { ...action.payload };

          const index = state.members.findIndex(
            (member) => member.id === newMember.id
          );
          if (index !== -1) {
            state.members[index] = newMember;
          } else {
            state.members.push(newMember);
          }
        }
      )
      .addCase(sendInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to send invitation';
      })
      .addCase(
        fetchInvitationDetails.fulfilled,
        (state, action: PayloadAction<Member>) => {
          const index = state.members.findIndex(
            (member) => member.id === action.payload.id
          );
          if (index !== -1) {
            // if expired_at is not null, then this is a an approved member and  then status is active, else pending
            action.payload.status = !_.isNil(action?.payload?.expires_at)
              ? 'pending'
              : 'active';
            state.members[index] = action.payload;
          }
        }
      )
      .addCase(
        updateInvitation.fulfilled,
        (state, action: PayloadAction<Member>) => {
          const updatedMember = {
            ...action.payload,
          };
          action.payload.status = 'pending';
          const index = state.members.findIndex(
            (member) => member.id === updatedMember.id
          );
          if (index !== -1) {
            state.members[index] = updatedMember;
          } else {
            state.members.push(updatedMember);
          }
        }
      )
      .addCase(destroyInvitation.pending, (state) => {
        state.isRemoving = true;
        state.error = null;
      })
      .addCase(
        destroyInvitation.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.isRemoving = false;
          state.members = state.members.filter(
            (member) =>
              member.id != action.payload && member.status !== 'pending'
          );
        }
      )
      .addCase(destroyInvitation.rejected, (state, action) => {
        state.isRemoving = false;
        state.error = action.payload as string;
      })
      .addCase(
        updateMember.fulfilled,
        (state, action: PayloadAction<Member>) => {
          const updatedMember: Member = {
            ...action.payload,
            status: 'pending',
          };
          const index = state.members.findIndex(
            (member) => member.id === updatedMember.id
          );
          if (index !== -1) {
            state.members[index] = updatedMember;
          } else {
            state.members.push(updatedMember);
          }
        }
      )
      .addCase(
        removeMember.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.members = state.members.filter(
            (member) => member.id !== action.payload
          );
        }
      )
      .addCase(
        fetchMemberDetails.fulfilled,
        (state, action: PayloadAction<Member>) => {
          const index = state.members.findIndex(
            (member) => member.id === action.payload.id
          );
          if (index !== -1) {
            state.members[index] = action.payload;
          }
        }
      )
      .addCase(
        fetchMemberPermissions.fulfilled,
        (state, action: PayloadAction<MemberPermissions>) => {
          state.selectedMemberPermissions = action.payload;
          // Update the corresponding member in the members array
          const index = state.members.findIndex(
            (member) => member.id === action.payload.user_id
          );
          if (index !== -1) {
            state.members[index] = {
              ...state.members[index],
              email: action.payload.email,
              role: action.payload.role,
              folder_ids: action.payload.folder_ids,
            };
          }
        }
      )
      .addCase(fetchMemberPermissions.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch member permissions';
      })
      .addCase(resendInvitation.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to resend invitation';
      })
      .addCase(fetchUserInvitations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchUserInvitations.fulfilled,
        (state, action: PayloadAction<InvitationsState>) => {
          state.isLoading = false;
          state.userInvitations = action.payload;
        }
      )
      .addCase(fetchUserInvitations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch invitations';
      })
      .addCase(acceptInvitation.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to accept invitation';
      });
  },
});

export default memberSlice.reducer;
